import { types, flow, applySnapshot } from "mobx-state-tree";
import { isUndefined } from "underscore";

import CONSTANTS from "const/constants";
import * as StreamAPI from "api/streams";
import { getContentMap } from "api/dvr";
import { isGrn } from "util/grn-utils";
import { hasChanged } from "util/mst-utils";

import DVRBlockCollection from "pages/trimmer/state/collection/dvrblocks";
import BaseBackboneModelMigrationStore from "pages/common/state/store/backbone-model-template";

const DEFAULTS = {
    FRAME_RATE: 25,
    FETCH_INTERVAL: 10000
};

const model = {
    streamId: types.identifier,
    cleanStreamId: types.maybe(types.string),
    aspectRatio: types.maybe(types.string),
    coverImage: types.maybe(types.string),
    createIdentity: types.maybe(types.string),
    creationDate: types.maybe(types.integer),
    derivedStreamEncodeState: types.maybe(types.string),
    displayName: types.maybe(types.string),
    displayedDvrStoreRetentionTime: types.maybe(types.integer),
    dvrStoreRetentionTime: types.maybe(types.integer),
    dvrStoreRollingTimeOffset: types.maybe(types.integer),
    encodeSource: types.maybe(types.string),
    encodingProfileId: types.maybe(types.string),
    epgId: types.maybe(types.string),
    exportedEncodeRecords: types.maybe(types.frozen()),

    isEnabled: types.maybe(types.boolean),
    isEpgEnabled: types.maybe(types.boolean),
    liveShareInfos: types.maybe(types.frozen()),
    mbrEncodingPresets: types.maybe(types.frozen({})),
    modifyDate: types.maybe(types.integer),
    modifyIdentity: types.maybe(types.string),
    organizationId: types.maybe(types.string),
    primaryMediafyKey: types.maybe(types.string),
    producerElasticEncodeState: types.maybe(types.string),
    producerEncode: types.maybe(types.frozen()),
    producerEncodeId: types.maybe(types.string),
    pushPublisherHlsPlaybackUrl: types.maybe(types.string),
    pushPublisherPlaybackUrl: types.maybe(types.string),
    scheduleDurationMinutes: types.maybe(types.number),
    scheduleCronExpression: types.maybe(types.string),
    scheduleEndDateTimeSeconds: types.maybe(types.number),
    scheduleStartDateTimeSeconds: types.maybe(types.number),
    scheduleTimeZone: types.maybe(types.string),
    scheduleType: types.maybe(types.string),
    secondaryMediafyKey: types.maybe(types.string),
    sourceHealth: types.maybe(types.string),
    sourceHlsUrl: types.maybe(types.string),
    sourceStreamUrlEditable: types.maybe(types.boolean),
    sourceStreamUrlVisible: types.maybe(types.boolean),
    sourceUrl: types.maybe(types.string),
    status: types.maybe(types.string),
    streamStatus: types.maybe(types.string),
    streamSubtype: types.maybe(types.string),
    udvrElasticEncodeState: types.maybe(types.string),
    udvrEncodeId: types.maybe(types.string),
    // These props are all depricated but are still needed for now
    primaryDvrStoreRollingTimeOffset: types.maybe(types.integer),
    primaryMediafyQueueName: types.maybe(types.string),
    primaryStreamName: types.maybe(types.string),
    primaryStreamServer: types.maybe(types.string),
    secondaryDvrStoreRollingTimeOffset: types.maybe(types.integer),
    secondaryMediafyQueueName: types.maybe(types.string),
    secondaryStreamName: types.maybe(types.string),
    secondaryStreamServer: types.maybe(types.string),
    version: types.maybe(
        types.union(...[1, 2].map((value) => types.literal(value)))
    )
};

const volatile = () => ({
    identifierAttribute: `streamId`,
    // used to handle live polling failure and stop a modal appearing multiple times
    failedDvrFetch: false,
    // used to be able to stop fetching stream data once started
    fetchIntervalID: ``,
    forceEnable: false,
    // this is used for setting the new part of the program to view and once migrated to react might not be needed
    programStartTime: undefined,
    // can be undefined, an integer, or Infinity
    programEndTime: undefined,
    // this is for switching between primary and secondary stream selection I'm not sure this is actually used anywhere
    streamSelection: ``
});

const views = (self) => ({
    get id() {
        return self.streamId;
    },

    get isCloudProducerEnabled() {
        return !isUndefined(self.producerEncodeId);
    },

    get streamType() {
        return isGrn(self.encodeSource)
            ? CONSTANTS.STREAM_TYPE.PUSH_STREAM
            : CONSTANTS.STREAM_TYPE.PULL_STREAM;
    },

    getDisplayName() {
        // Returns displayName if exists
        // fallback to streamName otherwise
        // and fallback to streamId if streamName is missing
        return self.displayName || self.primaryStreamName || self.streamId;
    },

    /**
     * @summary get the producerEncode
     * @returns {*} producer encode
     */
    getEncode() {
        return self.producerEncode;
    },

    /**
     * @summary Get the frame rate of the stream
     * @returns {number} frame rate
     */
    getFrameRate() {
        return (
            self.mbrEncodingPresets?.original?.videoFramesPerSecond ??
            DEFAULTS.FRAME_RATE
        );
    },

    /**
     * @summary Get the dvr rentention time
     * @returns {number} the rentention time in milliseconds
     */
    getDvrStoreRetentionTime() {
        // Always prefer display retention time, fall back to actual
        return (
            self.displayedDvrStoreRetentionTime ?? self.dvrStoreRetentionTime
        );
    }
});

const actions = (self) => ({
    /**
     * @summary Update model with new data (if changed)
     * @param {StreamObject} data
     */
    update(data) {
        if (Object.hasOwn(data, `streamId`)) {
            hasChanged(self, data) && applySnapshot(self, data);
        }
    },

    setFailedDvrFetch(value) {
        self.failedDvrFetch = value;
    },

    /**
     * @summary Set the id of the organization
     * @param {string} id
     */
    setOrganizationId(id) {
        self.organizationId = id;
    },

    /**
     * @summary Set the program start and end time
     * @param {integer} programStartTime
     * @param {integer} programEndTime
     */
    setProgramTime(programStartTime, programEndTime) {
        self.programStartTime = programStartTime;
        self.programEndTime = programEndTime;
    },

    /**
     * @summary Set the stream selection
     * @param {string} streamSelection
     */
    setStreamSelection(streamSelection) {
        self.streamSelection = streamSelection;
    },

    setVersion(newVersion) {
        self.version = newVersion;
    }
});

const asyncActions = (self) => {
    return {
        /**
         * Make an api call to GET this stream
         * @async
         * @param {Boolean} returnNode
         * @return {StreamObject | Error}
         */
        fetch: flow(function* fetch(returnNode = false) {
            try {
                const { data } = yield StreamAPI.getStream(self.streamId);
                applySnapshot(self, {
                    ...data
                });
                return returnNode ? self : data;
            } catch (error) {
                console.error(error);
                return error;
            }
        }),

        /**
         * Make an api call to GET the content map
         * @async
         * @param {object} options
         */
        select: flow(function* select(options) {
            options.streamId = self.streamId;
            self.setFailedDvrFetch(false);
            try {
                const contentMap = yield getContentMap(options);
                const streamVersion =
                    contentMap.contentSections?.[0]?.version || 1;
                self.setVersion(parseInt(streamVersion));

                const blocks = new DVRBlockCollection(
                    contentMap.contentSections,
                    {
                        parse: true,
                        startTimestamp: options.startTimestamp,
                        endTimestamp: options.endTimestamp,
                        streamId: self.streamId
                    }
                );

                // this is to send it to the correct video instance
                App.Events.trigger(`blocks:ready:${options.videoId}`, blocks);
            } catch (error) {
                console.log(error);
                if (!self.failedDvrFetch) {
                    App.Events.trigger(`contentmap:fail`, options);
                }
            }
        }),

        /**
         * @summary Update model with new data (if changed)
         * @param {StreamObject} streamData
         */
        updateStream: flow(function* updateStream(streamData) {
            try {
                const { data } = yield StreamAPI.updateStream(
                    self.streamId,
                    streamData
                );
                applySnapshot(self, {
                    ...data
                });
                return data;
            } catch (error) {
                console.error(error);
                return error;
            }
        }),

        updateEncodeSource: flow(function* updateEncodeSource(
            encodeSource,
            skipEncodeUrlSourceHealthcheck = false
        ) {
            const data = yield StreamAPI.updateEncodeSource(
                self.streamId,
                encodeSource,
                skipEncodeUrlSourceHealthcheck
            );

            self.encodeSource = data.encodeSource;
            self.sourceUrl = data.encodeSource;
            self.sourceHlsUrl = data.sourceHlsUrl;
        }),

        startFetching: function () {
            self.fetch();
            self.fetchIntervalID = setInterval(
                () => self.fetch(),
                DEFAULTS.FETCH_INTERVAL
            );
        },

        stopFetching: function () {
            clearInterval(self.fetchIntervalID);
        }
    };
};

const _StreamStore = types
    .model(`Stream`, model)
    .volatile(volatile)
    .views(views)
    .actions(actions)
    .actions(asyncActions);

export const StreamStore = BaseBackboneModelMigrationStore(_StreamStore);
